import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const searchData = [
  { title: 'GI Pathology', description: 'Our specialty: GI biopsies and resections', url: '/gi-pathology', keywords: 'gastrointestinal gi endoscopy colonoscopy barrett celiac ibd colitis polyp endoscopy center asc' },
  { title: 'Histology Training Program', description: 'State-licensed histology training', url: '/histology-training', keywords: 'histology histotechnician histotech training program career school license jobs openings indeed' },
  { title: 'Surgical Pathology', description: 'Examination of surgical specimens', url: '/services#surgical-pathology', keywords: 'surgery tissue biopsy prostate liver thyroid bladder gynecologic leep' },
  { title: 'Cytopathology', description: 'Analysis of cells from body fluids', url: '/services#cytopathology', keywords: 'fna pap smear cytology' },
  { title: 'Dermatopathology', description: 'Specialized skin disorder analysis', url: '/services#dermatopathology', keywords: 'skin melanoma' },
  { title: 'Immunohistochemistry', description: 'Advanced diagnostic technique', url: '/services#immunohistochemistry', keywords: 'ihc stain antibody' },
  { title: 'Expert Consultation', description: 'Second opinions on complex cases', url: '/services#consultation', keywords: 'second opinion' },
  { title: 'Quality & Accreditation', description: 'CLIA-certified, Joint Commission accredited', url: '/services#quality', keywords: 'clia accreditation accredited certified joint commission quality' },
  { title: 'Insurance We Accept', description: 'Medicare, Medicaid, TRICARE, and major health plans', url: '/referring-practices', keywords: 'insurance medicare medicaid tricare aetna cigna humana blue cross billing plans' },
  { title: 'Referring Practices', description: 'Partner with our lab — turnaround, logistics, and portal access', url: '/referring-practices', keywords: 'surgical center partner become client courier' },
  { title: 'Our Team', description: 'Meet our experienced pathologists', url: '/team', keywords: 'doctors pathologists' },
  { title: 'Albert Cohen, MD', description: 'Founder and President, GI Pathology', url: '/team', keywords: 'cohen' },
  { title: 'Robert Aportela, MD', description: 'GI Pathology', url: '/team', keywords: 'aportela' },
  { title: 'George Varsegi, MD', description: 'GI Pathology', url: '/team', keywords: 'varsegi' },
  { title: 'John Fortune, MD', description: 'Hematopathology, GI Pathology', url: '/team', keywords: 'fortune heme' },
  { title: 'Daniel Scharifker, MD', description: 'GI Pathology', url: '/team', keywords: 'scharifker' },
  { title: 'Contact Us', description: 'Phone, fax, and message the lab', url: '/contact', keywords: 'phone email fax address hours open' },
  { title: 'Privacy Policy', description: 'How this website handles visitor information', url: '/privacy-policy', keywords: 'privacy cookies analytics data' },
  { title: 'Provider Portal', description: 'Results and reports for clinicians', url: 'https://bdp.labdynamo.com', keywords: 'labdynamo login results' }
];

function SearchBar({ onNavigate }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(-1);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
        setActive(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runSearch = (value) => {
    if (!value.trim()) {
      setResults([]);
      return;
    }
    const q = value.toLowerCase();
    setResults(
      searchData.filter((item) =>
        item.title.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        (item.keywords || '').includes(q)
      )
    );
  };

  const goTo = (result) => {
    if (result.url.startsWith('http')) {
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } else {
      const [pathname, hash] = result.url.split('#');
      navigate({ pathname, hash: hash || '' });
    }
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setActive(-1);
    if (onNavigate) onNavigate();
  };

  const onChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    runSearch(value);
    setIsOpen(true);
    setActive(-1);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setActive(-1);
      return;
    }
    if (!isOpen || results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => (current + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => (current <= 0 ? results.length - 1 : current - 1));
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      goTo(results[active]);
    }
  };

  return (
    <div className="search-container" ref={searchRef}>
      <div className="search-input-wrapper">
        <input
          type="search"
          placeholder="Search"
          value={query}
          onChange={onChange}
          onFocus={() => query && setIsOpen(true)}
          onKeyDown={onKeyDown}
          className="search-input"
          role="combobox"
          aria-label="Search the site"
          aria-expanded={isOpen && (results.length > 0 || Boolean(query))}
          aria-controls="search-results"
          aria-activedescendant={active >= 0 ? `search-result-${active}` : undefined}
          aria-autocomplete="list"
          autoComplete="off"
        />
        <span className="search-button" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </span>
      </div>

      {isOpen && results.length > 0 && (
        <div className="search-results" id="search-results" role="listbox">
          {results.map((result, index) => (
            <button
              type="button"
              key={result.title}
              id={`search-result-${index}`}
              className={`search-result-item${index === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(index)}
              onClick={() => goTo(result)}
              role="option"
              aria-selected={index === active}
            >
              <h4>{result.title}</h4>
              <p>{result.description}</p>
            </button>
          ))}
        </div>
      )}

      {isOpen && query && results.length === 0 && (
        <div className="search-results" id="search-results">
          <div className="search-result-item no-results">
            <p>No results for “{query}”</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchBar;
